/**
 * Scheduler tab — admin-only.
 *
 * One card per scheduled job, grouped by the app that owns it. A card shows
 * enough to answer "did it run, and when does it run next?" without expanding;
 * expanding adds what it actually does, the schedule editor, its knobs, and the
 * run history.
 *
 * Schedule and config edits are explicit — you change the fields, then press
 * Save. Auto-saving something that starts and stops scheduled financial work on
 * every keystroke would be the wrong instinct.
 */
import { useCallback, useEffect, useState } from 'react'
import { ApiError } from '../api'
import {
  DAY_LABELS,
  formatDuration,
  getHistory,
  listTriggers,
  patchTrigger,
  relativeTime,
  runTriggerNow,
  type ConfigControl,
  type HistoryEntry,
  type IntervalUnit,
  type Schedule,
  type SchedulerApp,
  type Trigger,
} from '../scheduler/api'

// ─── schedule editor ─────────────────────────────────────────────────────────

function ScheduleEditor({
  schedule,
  disabled,
  onSave,
}: {
  schedule: Schedule
  disabled: boolean
  onSave: (s: Schedule) => Promise<void>
}) {
  const [draft, setDraft] = useState<Schedule>(schedule)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // Re-sync when the server hands back a different schedule (e.g. after a save
  // elsewhere, or a reload).
  useEffect(() => { setDraft(schedule) }, [schedule])

  const dirty = JSON.stringify(draft) !== JSON.stringify(schedule)

  async function save() {
    setSaving(true)
    setErr(null)
    try {
      await onSave(draft)
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  if (draft.type === 'on-event') {
    return (
      <p className="muted">
        Fired by the application when <strong>{draft.eventLabel || draft.event}</strong> happens.
        There is no clock schedule to edit.
      </p>
    )
  }

  return (
    <div className="sched-editor">
      <div className="sched-editor__row">
        <label className="sched-label" htmlFor="sched-type">Runs</label>
        <select
          id="sched-type"
          className="select"
          disabled={disabled || saving}
          value={draft.type}
          onChange={(e) =>
            setDraft(
              e.target.value === 'interval'
                ? { type: 'interval', interval: { every: 30, unit: 'minutes' } }
                : { type: 'time-of-day', times: ['08:00'] },
            )
          }
        >
          <option value="interval">On an interval</option>
          <option value="time-of-day">At set times</option>
        </select>
      </div>

      {draft.type === 'interval' ? (
        <div className="sched-editor__row">
          <label className="sched-label" htmlFor="sched-every">Every</label>
          <input
            id="sched-every"
            className="sched-input sched-input--num"
            type="number"
            min={1}
            disabled={disabled || saving}
            value={draft.interval.every}
            onChange={(e) =>
              setDraft({
                type: 'interval',
                interval: { every: Number(e.target.value) || 1, unit: draft.interval.unit },
              })
            }
          />
          <select
            className="select"
            disabled={disabled || saving}
            value={draft.interval.unit}
            onChange={(e) =>
              setDraft({
                type: 'interval',
                interval: { every: draft.interval.every, unit: e.target.value as IntervalUnit },
              })
            }
          >
            <option value="minutes">minutes</option>
            <option value="hours">hours</option>
            <option value="days">days</option>
          </select>
          {draft.interval.unit === 'minutes' && draft.interval.every < 5 && (
            <span className="sched-warn">
              The heartbeat only arrives every 5 minutes — anything shorter cannot be honoured.
            </span>
          )}
        </div>
      ) : (
        <>
          <div className="sched-editor__row">
            <label className="sched-label" htmlFor="sched-times">At</label>
            <input
              id="sched-times"
              className="sched-input"
              type="text"
              placeholder="08:00, 17:30"
              disabled={disabled || saving}
              value={draft.times.join(', ')}
              onChange={(e) =>
                setDraft({
                  type: 'time-of-day',
                  times: e.target.value.split(',').map((t) => t.trim()).filter(Boolean),
                  days: draft.days,
                })
              }
            />
            <span className="muted">24-hour, London time</span>
          </div>
          <div className="sched-editor__row">
            <span className="sched-label">On</span>
            <div className="sched-days">
              {DAY_LABELS.map((d) => {
                const active = !draft.days || draft.days.length === 0 || draft.days.includes(d.key)
                return (
                  <button
                    key={d.key}
                    type="button"
                    className={`sched-day${active ? ' active' : ''}`}
                    disabled={disabled || saving}
                    onClick={() => {
                      const current = draft.days && draft.days.length ? draft.days : DAY_LABELS.map((x) => x.key)
                      const next = current.includes(d.key)
                        ? current.filter((k) => k !== d.key)
                        : [...current, d.key]
                      setDraft({ type: 'time-of-day', times: draft.times, days: next })
                    }}
                  >
                    {d.label}
                  </button>
                )
              })}
            </div>
          </div>
        </>
      )}

      {err && <p className="alert alert--error">{err}</p>}

      <div className="sched-editor__actions">
        <button className="btn" type="button" disabled={disabled || saving || !dirty} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save schedule'}
        </button>
        {dirty && !saving && (
          <button className="btn" type="button" onClick={() => setDraft(schedule)}>Discard</button>
        )}
      </div>
    </div>
  )
}

// ─── config editor ───────────────────────────────────────────────────────────

function ConfigEditor({
  controls,
  disabled,
  onSave,
}: {
  controls: ConfigControl[]
  disabled: boolean
  onSave: (values: Record<string, unknown>) => Promise<void>
}) {
  const initial = useCallback(
    () => Object.fromEntries(controls.map((c) => [c.key, c.value])) as Record<string, unknown>,
    [controls],
  )
  const [draft, setDraft] = useState<Record<string, unknown>>(initial)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => { setDraft(initial()) }, [initial])

  const dirty = JSON.stringify(draft) !== JSON.stringify(initial())
  const set = (k: string, v: unknown) => setDraft((d) => ({ ...d, [k]: v }))

  async function save() {
    setSaving(true)
    setErr(null)
    try {
      await onSave(draft)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setSaving(false)
    }
  }

  if (controls.length === 0) return <p className="muted">This job has no settings.</p>

  return (
    <div className="sched-config">
      {controls.map((c) => (
        <div key={c.key} className="sched-config__row">
          <label className="sched-label" htmlFor={`cfg-${c.key}`}>{c.label}</label>
          {c.type === 'toggle' ? (
            <input
              id={`cfg-${c.key}`}
              type="checkbox"
              disabled={disabled || saving}
              checked={Boolean(draft[c.key])}
              onChange={(e) => set(c.key, e.target.checked)}
            />
          ) : c.type === 'number' ? (
            <input
              id={`cfg-${c.key}`}
              className="sched-input sched-input--num"
              type="number"
              min={c.min}
              max={c.max}
              step={c.step}
              disabled={disabled || saving}
              value={String(draft[c.key] ?? '')}
              onChange={(e) => set(c.key, e.target.value === '' ? null : Number(e.target.value))}
            />
          ) : c.type === 'select' ? (
            <select
              id={`cfg-${c.key}`}
              className="select"
              disabled={disabled || saving}
              value={String(draft[c.key] ?? '')}
              onChange={(e) => set(c.key, e.target.value)}
            >
              {(c.options ?? []).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          ) : (
            <input
              id={`cfg-${c.key}`}
              className="sched-input"
              type="text"
              disabled={disabled || saving}
              value={String(draft[c.key] ?? '')}
              onChange={(e) => set(c.key, e.target.value)}
            />
          )}
          {c.unit && <span className="muted">{c.unit}</span>}
          {c.help && <p className="sched-help">{c.help}</p>}
        </div>
      ))}

      {err && <p className="alert alert--error">{err}</p>}

      <div className="sched-editor__actions">
        <button className="btn" type="button" disabled={disabled || saving || !dirty} onClick={() => void save()}>
          {saving ? 'Saving…' : 'Save settings'}
        </button>
        {dirty && !saving && (
          <button className="btn" type="button" onClick={() => setDraft(initial())}>Discard</button>
        )}
      </div>
    </div>
  )
}

// ─── job card ────────────────────────────────────────────────────────────────

function JobCard({
  trigger,
  onChanged,
}: {
  trigger: Trigger
  onChanged: (t: Trigger) => void
}) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState<null | 'toggle' | 'run'>(null)
  const [err, setErr] = useState<string | null>(null)
  const [history, setHistory] = useState<HistoryEntry[] | null>(null)
  const [historyErr, setHistoryErr] = useState<string | null>(null)

  const external = trigger.managed === 'external'

  const loadHistory = useCallback(async () => {
    setHistoryErr(null)
    try {
      const r = await getHistory(trigger.id)
      setHistory(r.history)
    } catch (e) {
      setHistoryErr(e instanceof Error ? e.message : String(e))
    }
  }, [trigger.id])

  useEffect(() => {
    if (open && history === null) void loadHistory()
  }, [open, history, loadHistory])

  async function toggle() {
    setBusy('toggle')
    setErr(null)
    try {
      const r = await patchTrigger(trigger.id, { enabled: !trigger.enabled })
      onChanged(r.trigger)
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  async function runNow() {
    setBusy('run')
    setErr(null)
    try {
      const r = await runTriggerNow(trigger.id)
      onChanged({ ...trigger, lastRun: r.lastRun })
      if (open) await loadHistory()
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  const last = trigger.lastRun

  return (
    <div className="job" data-enabled={trigger.enabled}>
      <div className="job__head">
        <div className="job__ident">
          <h3>
            {trigger.name}
            {external && <span className="tag">external</span>}
            {!trigger.enabled && <span className="tag">paused</span>}
          </h3>
          <p className="job__action">{trigger.action}</p>
          <p className="job__meta mono">{trigger.id}</p>
        </div>

        <div className="job__state">
          <div className="job__stat">
            <span className="job__stat-label">Last run</span>
            {last ? (
              <span>
                <span className="run-pill" data-status={last.status}>{last.status}</span>{' '}
                {relativeTime(last.at)}
                <span className="muted"> · {formatDuration(last.durationMs)}</span>
              </span>
            ) : (
              <span className="muted">never</span>
            )}
          </div>
          <div className="job__stat">
            <span className="job__stat-label">Next run</span>
            <span>{trigger.nextRun}</span>
          </div>
          <div className="job__stat">
            <span className="job__stat-label">Schedule</span>
            <span>{trigger.scheduleSummary}</span>
          </div>
        </div>
      </div>

      {last?.message && <p className="job__lastmsg">{last.message}</p>}

      {err && <p className="alert alert--error">{err}</p>}

      <div className="job__actions">
        <button className="btn" type="button" disabled={external || busy !== null} onClick={() => void toggle()}>
          {busy === 'toggle' ? 'Saving…' : trigger.enabled ? 'Pause' : 'Resume'}
        </button>
        <button
          className="btn"
          type="button"
          disabled={external || !trigger.runnable || busy !== null}
          title={!trigger.runnable ? 'This trigger has no runnable handler in this build' : undefined}
          onClick={() => void runNow()}
        >
          {busy === 'run' ? 'Running…' : 'Run now'}
        </button>
        <button className="btn" type="button" onClick={() => setOpen((o) => !o)}>
          {open ? 'Hide details' : 'Details'}
        </button>
      </div>

      {open && (
        <div className="job__detail">
          <section>
            <h4>What it does</h4>
            <p>{trigger.description}</p>
            <p className="sched-does mono">{trigger.does}</p>
          </section>

          <section>
            <h4>If it breaks</h4>
            <p>
              <span className="run-pill" data-sev={trigger.impact.severity}>{trigger.impact.severity}</span>{' '}
              Affects <strong>{trigger.impact.audience}</strong>
            </p>
            <p className="muted">Touches: {trigger.impact.systems.join(', ')}</p>
            {trigger.impact.notes && <p>{trigger.impact.notes}</p>}
            <p className="muted">Abandoned if it runs longer than {trigger.timeoutSec}s.</p>
          </section>

          <section>
            <h4>Schedule</h4>
            <ScheduleEditor
              schedule={trigger.schedule}
              disabled={external}
              onSave={async (s) => {
                const r = await patchTrigger(trigger.id, { schedule: s })
                onChanged(r.trigger)
              }}
            />
          </section>

          <section>
            <h4>Settings</h4>
            <ConfigEditor
              controls={trigger.config}
              disabled={external}
              onSave={async (values) => {
                const r = await patchTrigger(trigger.id, { config: values })
                onChanged(r.trigger)
              }}
            />
          </section>

          <section>
            <h4>Recent runs</h4>
            {historyErr && <p className="alert alert--error">{historyErr}</p>}
            {history === null && !historyErr && <p className="muted">Loading…</p>}
            {history?.length === 0 && <p className="muted">No runs recorded yet.</p>}
            {history && history.length > 0 && (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr><th>When</th><th>Status</th><th>Took</th><th>Message</th></tr>
                  </thead>
                  <tbody>
                    {history.map((h, i) => (
                      <tr key={`${h.at}-${i}`}>
                        <td>{relativeTime(h.at)}</td>
                        <td><span className="run-pill" data-status={h.status}>{h.status}</span></td>
                        <td>{formatDuration(h.durationMs)}</td>
                        <td className="job__histmsg">{h.message || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section>
            <h4>Version</h4>
            <p className="muted">
              v{trigger.version} · updated {trigger.updatedAt} by {trigger.updatedBy}
              {!trigger.seeded && ' · running on registry defaults (no saved state yet)'}
            </p>
            <ul className="sched-changelog">
              {trigger.changelog.map((c) => (
                <li key={`${c.v}-${c.at}`}><strong>v{c.v}</strong> <span className="muted">{c.at}</span> — {c.note}</li>
              ))}
            </ul>
          </section>
        </div>
      )}
    </div>
  )
}

// ─── page ────────────────────────────────────────────────────────────────────

export default function Scheduler() {
  const [apps, setApps] = useState<SchedulerApp[]>([])
  const [triggers, setTriggers] = useState<Trigger[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setError(null)
    try {
      const r = await listTriggers()
      setApps(r.apps)
      setTriggers(r.triggers)
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const onChanged = (t: Trigger) =>
    setTriggers((list) => list.map((x) => (x.id === t.id ? t : x)))

  if (loading) return <div className="panel"><p className="muted">Loading scheduled tasks…</p></div>

  const failing = triggers.filter((t) => t.enabled && t.lastRun?.status === 'failed')

  return (
    <div className="sched-page">
      <div className="panel">
        <div className="panel__head">
          <div>
            <h2>Scheduler</h2>
            <p className="panel__sub">
              Scheduled work for the finance platform. A heartbeat arrives every 5 minutes and runs
              whatever is due — so a job set to run "every 30 minutes" runs within 5 minutes of that,
              not to the second. All times are London.
            </p>
          </div>
          <button className="btn" type="button" onClick={() => void load()}>Refresh</button>
        </div>

        {error && <p className="alert alert--error">{error}</p>}

        {failing.length > 0 && (
          <p className="alert alert--error">
            {failing.length} job{failing.length === 1 ? '' : 's'} last failed:{' '}
            {failing.map((t) => t.name).join(', ')}. Open the job for the error and its history.
          </p>
        )}

        {!error && failing.length === 0 && triggers.length > 0 && (
          <p className="alert alert--ok">All {triggers.length} scheduled job{triggers.length === 1 ? '' : 's'} healthy.</p>
        )}
      </div>

      {apps.map((app) => {
        const own = triggers.filter((t) => t.app === app.key)
        if (own.length === 0) return null
        return (
          <div key={app.key} className="panel">
            <div className="panel__head">
              <div>
                <h2>{app.name}</h2>
                <p className="panel__sub">{app.blurb}</p>
              </div>
            </div>
            <div className="job-list">
              {own.map((t) => <JobCard key={t.id} trigger={t} onChanged={onChanged} />)}
            </div>
          </div>
        )
      })}

      {triggers.length === 0 && !error && (
        <div className="empty">
          <p>No scheduled jobs are registered yet.</p>
          <p className="muted">Jobs are declared in api/src/scheduler/registry.ts.</p>
        </div>
      )}
    </div>
  )
}
